import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { leadsAPI, notesAPI, usersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  LogOut, Users, TrendingUp, Clock, CheckCircle, Filter, Eye, MessageSquare, Trash2
} from 'lucide-react';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ lead_type: 'all', status: 'all' });
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const filterParams = {};
      if (filters.lead_type && filters.lead_type !== 'all') filterParams.lead_type = filters.lead_type;
      if (filters.status && filters.status !== 'all') filterParams.status = filters.status;
      
      const [leadsRes, statsRes, usersRes] = await Promise.all([
        leadsAPI.getLeads(filterParams),
        leadsAPI.getStats(),
        usersAPI.getUsers()
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadsAPI.updateLead(leadId, { status: newStatus });
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleAssignUser = async (leadId, userId) => {
    try {
      await leadsAPI.updateLead(leadId, { assigned_to: userId });
      toast.success('Lead asignado correctamente');
      loadData();
    } catch (error) {
      toast.error('Error al asignar lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    
    try {
      await leadsAPI.deleteLead(leadId);
      toast.success('Lead eliminado');
      loadData();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Error al eliminar lead');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;

    try {
      await notesAPI.createNote(selectedLead.id, newNote);
      toast.success('Nota agregada');
      setNewNote('');
      
      // Reload lead details
      const leadRes = await leadsAPI.getLead(selectedLead.id);
      setSelectedLead(leadRes.data);
      loadData();
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      nuevo: 'bg-blue-500',
      contactado: 'bg-yellow-500',
      en_evaluacion: 'bg-purple-500',
      reunion_agendada: 'bg-orange-500',
      cliente: 'bg-green-500',
      descartado: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      jugador: 'bg-gold-light text-black',
      agente: 'bg-purple-600',
      club: 'bg-blue-600',
      otro: 'bg-gray-600'
    };
    return colors[type] || 'bg-gray-600';
  };

  const formatStatus = (status) => {
    const labels = {
      nuevo: 'Nuevo',
      contactado: 'Contactado',
      en_evaluacion: 'En Evaluación',
      reunion_agendada: 'Reunión Agendada',
      cliente: 'Cliente',
      descartado: 'Descartado'
    };
    return labels[status] || status;
  };

  const formatType = (type) => {
    const labels = {
      jugador: 'Jugador/a',
      agente: 'Agente',
      club: 'Club',
      otro: 'Otro'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !stats) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="dashboard-brand">
            <img src="/ikons-logo.png" alt="Ikons Agency CRM" className="dashboard-logo-image" />
            <div>
              <p className="dashboard-subtitle">CRM</p>
              <p className="dashboard-user">Bienvenido, {user?.full_name}</p>
            </div>
          </div>
          <Button onClick={logout} variant="outline" className="btn-logout">
            <LogOut className="icon-sm" />
            Cerrar Sesión
          </Button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <Card className="stat-card">
          <CardHeader className="stat-header">
            <Users className="stat-icon" />
            <CardTitle className="stat-title">Total Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="stat-value">{stats?.total || 0}</p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader className="stat-header">
            <TrendingUp className="stat-icon gold" />
            <CardTitle className="stat-title">Jugadores</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="stat-value">{stats?.by_type?.jugador || 0}</p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader className="stat-header">
            <Clock className="stat-icon blue" />
            <CardTitle className="stat-title">Agentes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="stat-value">{stats?.by_type?.agente || 0}</p>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardHeader className="stat-header">
            <CheckCircle className="stat-icon green" />
            <CardTitle className="stat-title">Clientes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="stat-value">{stats?.by_status?.cliente || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="icon-sm" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent className="filters-content">
          <div className="filter-group">
            <label className="filter-label">Tipo de Lead</label>
            <Select value={filters.lead_type || 'all'} onValueChange={(value) => setFilters({ ...filters, lead_type: value === 'all' ? '' : value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="jugador">Jugador/a</SelectItem>
                <SelectItem value="agente">Agente</SelectItem>
                <SelectItem value="club">Club</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Estado</label>
            <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="nuevo">Nuevo</SelectItem>
                <SelectItem value="contactado">Contactado</SelectItem>
                <SelectItem value="en_evaluacion">En Evaluación</SelectItem>
                <SelectItem value="reunion_agendada">Reunión Agendada</SelectItem>
                <SelectItem value="cliente">Cliente</SelectItem>
                <SelectItem value="descartado">Descartado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {(filters.lead_type !== 'all' || filters.status !== 'all') && (
            <Button 
              variant="outline" 
              onClick={() => setFilters({ lead_type: 'all', status: 'all' })}
              className="btn-reset-filters"
            >
              Limpiar Filtros
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="leads-table-card">
        <CardHeader>
          <CardTitle>Leads ({leads.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="table-loading">Cargando leads...</div>
          ) : leads.length === 0 ? (
            <div className="table-empty">
              <Users className="empty-icon" />
              <p>No hay leads que coincidan con los filtros</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Contacto</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Asignado a</th>
                    <th>Fecha</th>
                    <th>Notas</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id} className="lead-row">
                      <td className="lead-name">{lead.name}</td>
                      <td className="lead-contact">{lead.contact}</td>
                      <td>
                        <Badge className={getTypeBadgeColor(lead.lead_type)}>
                          {formatType(lead.lead_type)}
                        </Badge>
                      </td>
                      <td>
                        <Select 
                          value={lead.status} 
                          onValueChange={(value) => handleStatusChange(lead.id, value)}
                        >
                          <SelectTrigger className="status-select">
                            <Badge className={getStatusBadgeColor(lead.status)}>
                              {formatStatus(lead.status)}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nuevo">Nuevo</SelectItem>
                            <SelectItem value="contactado">Contactado</SelectItem>
                            <SelectItem value="en_evaluacion">En Evaluación</SelectItem>
                            <SelectItem value="reunion_agendada">Reunión Agendada</SelectItem>
                            <SelectItem value="cliente">Cliente</SelectItem>
                            <SelectItem value="descartado">Descartado</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <Select 
                          value={lead.assigned_to || 'unassigned'} 
                          onValueChange={(value) => handleAssignUser(lead.id, value === 'unassigned' ? null : value)}
                        >
                          <SelectTrigger className="assign-select">
                            <SelectValue placeholder="Sin asignar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unassigned">Sin asignar</SelectItem>
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.full_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="lead-date">{formatDate(lead.created_at)}</td>
                      <td className="lead-notes-count">
                        <MessageSquare className="icon-xs" />
                        {lead.notes?.length || 0}
                      </td>
                      <td>
                        <Dialog open={isDialogOpen && selectedLead?.id === lead.id} onOpenChange={setIsDialogOpen}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSelectedLead(lead)}
                            >
                              <Eye className="icon-xs" />
                              Ver
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="lead-dialog">
                            <DialogHeader>
                              <DialogTitle>Detalles del Lead</DialogTitle>
                            </DialogHeader>
                            {selectedLead && (
                              <div className="lead-details">
                                <div className="detail-section">
                                  <h3 className="detail-title">Información</h3>
                                  <div className="detail-grid">
                                    <div>
                                      <strong>Nombre:</strong> {selectedLead.name}
                                    </div>
                                    <div>
                                      <strong>Contacto:</strong> {selectedLead.contact}
                                    </div>
                                    <div>
                                      <strong>Tipo:</strong> {formatType(selectedLead.lead_type)}
                                    </div>
                                    <div>
                                      <strong>Estado:</strong> {formatStatus(selectedLead.status)}
                                    </div>
                                  </div>
                                  <div className="detail-message">
                                    <strong>Mensaje:</strong>
                                    <p>{selectedLead.message}</p>
                                  </div>
                                </div>

                                <div className="detail-section">
                                  <h3 className="detail-title">Notas ({selectedLead.notes?.length || 0})</h3>
                                  <div className="notes-list">
                                    {selectedLead.notes?.map((note) => (
                                      <div key={note.id} className="note-item">
                                        <div className="note-header">
                                          <span className="note-author">{note.created_by_name}</span>
                                          <span className="note-date">{formatDate(note.created_at)}</span>
                                        </div>
                                        <p className="note-content">{note.content}</p>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  <div className="add-note">
                                    <Textarea
                                      value={newNote}
                                      onChange={(e) => setNewNote(e.target.value)}
                                      placeholder="Agregar una nota..."
                                      rows={3}
                                    />
                                    <Button onClick={handleAddNote} className="btn-add-note">
                                      Agregar Nota
                                    </Button>
                                  </div>
                                </div>

                                <div className="detail-actions">
                                  <Button 
                                    variant="destructive" 
                                    onClick={() => handleDeleteLead(selectedLead.id)}
                                  >
                                    <Trash2 className="icon-xs" />
                                    Eliminar Lead
                                  </Button>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
